import {
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Text,
  Textarea,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { Field } from 'formik';
import type React from 'react';

export interface TextAreaWithCheckboxFieldProps {
  id?: string;
  label?: string;
  extra?: string;
  placeholder?: string;
  name: string;
  error?: string;
  errors?: string;
  touched?: boolean;
  isRequired?: boolean;
  full?: boolean;
  rows?: number;
  labelFontSize?: string;
  labelMb?: string;
  isDisabled?: boolean;
  tooltipElement?: React.ReactNode;
  checkboxLabel?: string;
  value?: { value: string; checkVal: boolean };
  helpers?: {
    setValue: (value: any) => void;
    setTouched: (touched: boolean) => void;
    setError: (error: string) => void;
  };
  field?: any;
  [x: string]: any;
}

export function TextAreaWithCheckboxField(props: TextAreaWithCheckboxFieldProps) {
  const {
    mb = 6,
    id,
    label,
    extra,
    error,
    errors,
    touched,
    placeholder,
    isRequired,
    name,
    full,
    rows = 5,
    labelFontSize,
    labelMb,
    isDisabled,
    tooltipElement,
    checkboxLabel = 'Enable checkbox',
    value = { value: '', checkVal: false },
    helpers,
    field,
    maxW,
    minW,
    onDark,
    ...rest
  } = props;

  const [lightColor, darkColor] = onDark
    ? ['white', 'chakra-body-text']
    : ['chakra-body-text', 'white'];

  const textColorPrimary = useColorModeValue(lightColor, darkColor);
  const bgPrimary = useColorModeValue('transparent', 'navy.800');
  const borderPrimary = useColorModeValue('gray.600', 'whiteAlpha.100');
  const isError = !!(error && error.length > 0) || !!(errors && errors.length > 0);

  return (
    <Flex direction="column" mb={mb || '0'} minW={minW} maxW={maxW}>
      <FormControl isRequired={isRequired} isInvalid={isError} isDisabled={isDisabled}>
        {!!label && (
          <HStack justifyContent="space-between">
            <FormLabel
              display="flex"
              mb={labelMb || 6}
              htmlFor={id}
              fontSize={labelFontSize || '2xl'}
              color={textColorPrimary}
              fontWeight="bold"
              _hover={{ cursor: 'pointer' }}
            >
              {label}
              <Text fontSize="sm" fontWeight="normal" ms="2px">
                {extra}
              </Text>
            </FormLabel>
            {tooltipElement && tooltipElement}
          </HStack>
        )}

        <VStack spacing={4} align="stretch">
          <Field name={name}>
            {({ field: formikField }: any) => (
              <Textarea
                id={id}
                placeholder={placeholder}
                value={formikField.value?.value || ''}
                onChange={(e) => {
                  const newValue = {
                    value: e.target.value,
                    checkVal: formikField.value?.checkVal || false,
                  };
                  helpers?.setValue(newValue);
                }}
                color={textColorPrimary}
                outline={isError || touched ? '1px solid red' : 'unset'}
                fontSize="xl"
                bg={bgPrimary}
                border="2px solid "
                borderColor={borderPrimary}
                p="33px"
                borderRadius="10px"
                rows={rows}
                isDisabled={isDisabled}
                {...rest}
                _placeholder={{ color: 'gray.600' }}
              />
            )}
          </Field>

          <Field name={name}>
            {({ field: formikField }: any) => (
              <Checkbox
                isChecked={formikField.value?.checkVal || false}
                onChange={(e) => {
                  const newValue = {
                    value: formikField.value?.value || '',
                    checkVal: e.target.checked,
                  };
                  helpers?.setValue(newValue);
                }}
                isDisabled={isDisabled}
                colorScheme="primary"
                fontSize="lg"
                size="lg"
                iconColor="primaryText"
              >
                {checkboxLabel}
              </Checkbox>
            )}
          </Field>
        </VStack>

        {isError && touched && <FormErrorMessage ml="10px">{error || errors}</FormErrorMessage>}
      </FormControl>
    </Flex>
  );
}
